import { Link } from "react-router-dom";
import { Input } from "@/components/Base/Input";
import { StaticColorDialog } from "@/components/Colors/ColorDisplay";
import { Feature } from "flagged";
import { CardFormDialog } from "@/components/Dashboard/components/Card/CardFormDialog";
import { InboxTrigger } from "@/components/Common/InboxTrigger";
import { TasksBellTrigger } from "@/components/Common/TasksBellTrigger";
import { CreateCardDto, IconNames } from "@/types/v2";
import { card } from "@/repositories/cards";
import { markCardAsComplete } from "@/lib/cards";
import { cn, firestoreToDateTime } from "@/lib/utils";
import { useEffect, useMemo } from "react";
import { useRoles } from "@/hooks/useRoles";
import { useStore } from "@/stores/useStore";
import { useCardFiltering } from "@/hooks/useCardFiltering";
import { useRoPanelParams } from "@/hooks/useRoPanelParams";
import { useLayouts } from "@/hooks/useLayouts";
import { Icon } from "@/components/Common/Icon";
import { useFeatures } from "@/hooks/useFeatures";
import { DashboardFilters } from "@/components/Dashboard/DashboardFilters";
import { LayoutSelector } from "@/components/Dashboard/LayoutSelector";
import { CardSizeControl } from "@/components/Dashboard/CardSizeControl";
import { ContainerHeaderSizeToggle } from "@/components/Dashboard/ContainerHeaderSizeToggle";
import { SyncStatus } from "@/components/Dashboard/SyncStatus";
import { LabelSyncStatus } from "@/components/Dashboard/LabelSyncStatus";
import { TagsRepository } from "@/repositories/tags";
import { useTerminology } from "@/hooks/useTerminology";
import { DEFAULT_COLORS } from "@/lib/const";
import { useServiceAdvisors } from "@/hooks/shop/useServiceAdvisors";
import { useTechnicians } from "@/hooks/shop/useTechnicians";
import { ViewToggle } from "@/components/Common/ViewToggle";
import { useShopConfig } from "@/hooks/shop/useShopConfig";
import { useCustomerUpdates } from "@/hooks/useCustomerUpdates";

export const DashboardHeader = () => {
  useTerminology();
  const shop = useStore((s) => s.shop);

  const serviceAdvisors = useServiceAdvisors();

  const technicians = useTechnicians();

  const { authState, shopId, dashboardLayoutId, setDashboardLayoutId, tags } =
    useStore();

  const shopConfig = useShopConfig();
  const { isCustomerUpdatesEnabled } = useCustomerUpdates();

  // Check if at least one inbox panel tab is visible
  const showInboxPanel = useMemo(() => {
    const config = shopConfig?.inboxPanel;
    const showActivityFeed = config?.showActivityFeed ?? true;
    const showMessages = config?.showMessages ?? true;
    const showGoList = config?.showGoList ?? true;
    const showCustomerUpdates =
      (config?.showCustomerUpdates ?? true) && isCustomerUpdatesEnabled;

    return (
      showActivityFeed || showMessages || showGoList || showCustomerUpdates
    );
  }, [shopConfig, isCustomerUpdatesEnabled]);

  const {
    search,
    updateCardSearch,
    filteringTechnician,
    updateFilteringTechnician,
    filteringServiceAdvisor,
    updateFilteringServiceAdvisor,
    dueInStart,
    updateDueInStart,
    dueInEnd,
    updateDueInEnd,
    dueOutStart,
    updateDueOutStart,
    dueOutEnd,
    updateDueOutEnd,
    apptStartStart,
    updateApptStartStart,
    apptStartEnd,
    updateApptStartEnd,
    apptEndStart,
    updateApptEndStart,
    apptEndEnd,
    updateApptEndEnd,
    filterTags,
    updateTags,
    appliedFiltersCount,
    filterColors,
    updateColors,
    filterContainer,
    updateContainer,
  } = useCardFiltering();
  const { submitSearch, clearSearchResults } = useRoPanelParams();
  const { layouts: filteredLayoutsData } = useLayouts();

  const { features } = useFeatures(shop);

  const { isViewOnly, isTechnician } = useRoles();

  const handleServiceWriterChange = (value: string | string[]) => {
    updateFilteringServiceAdvisor(value);
  };

  const handleTechnicianChange = (value: string | string[]) => {
    updateFilteringTechnician(value);
  };

  const handleDueInStartChange = (value: string | undefined) => {
    updateDueInStart(value);
  };

  const handleDueInEndChange = (value: string | undefined) => {
    updateDueInEnd(value);
  };

  const handleDueOutStartChange = (value: string | undefined) => {
    updateDueOutStart(value);
  };

  const handleDueOutEndChange = (value: string | undefined) => {
    updateDueOutEnd(value);
  };

  const handleApptStartStartChange = (value: string | undefined) => {
    updateApptStartStart(value);
  };

  const handleApptStartEndChange = (value: string | undefined) => {
    updateApptStartEnd(value);
  };

  const handleApptEndStartChange = (value: string | undefined) => {
    updateApptEndStart(value);
  };

  const handleApptEndEndChange = (value: string | undefined) => {
    updateApptEndEnd(value);
  };

  const handleFilterTagsChange = (value: string[] | undefined) => {
    updateTags(value);
  };

  const handleFilterColorsChange = (value: string[] | undefined) => {
    updateColors(value);
  };

  const handleFilterContainerChange = (value: string[]) => {
    updateContainer(value);
  };

  const handleLayoutSelect = (value: string) => {
    if (!value) return;
    setDashboardLayoutId(value);
  };

  const handleSearchStringChange = (value: string) => {
    updateCardSearch(value);
    if (!value.trim()) {
      clearSearchResults();
    }
  };

  const handleClearSearch = () => {
    updateCardSearch("");
    clearSearchResults();
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      submitSearch(search);
    }
  };

  const handleManualOnSubmit = async (values: CreateCardDto) => {
    if (!shopId) return;
    if (values.tags?.length && values.tags?.length > 0) {
      await TagsRepository.addTag(shopId, values.tags, tags);
    }
    const newCard = await card.create(shopId, values);

    // If completedDate is set, mark the card as complete
    if (values.completedDate && newCard?.id) {
      const completedDate = firestoreToDateTime(values.completedDate);
      await markCardAsComplete(shopId, newCard.id, completedDate);
    }
    return;
  };

  useEffect(() => {
    const layoutNames = filteredLayoutsData.map((obj) => Object.keys(obj)[0]);

    if (
      dashboardLayoutId &&
      layoutNames.length > 0 &&
      !layoutNames.includes(dashboardLayoutId ?? "")
    ) {
      setDashboardLayoutId(layoutNames[0]);
    }
  }, [dashboardLayoutId, filteredLayoutsData, setDashboardLayoutId]);

  return (
    <div className="flex flex-row items-center justify-between gap-4">
      <div className="flex flex-row items-center gap-2">
        {authState === "unauthenticated" && (
          <Link className="px-10" to="">
            Login
          </Link>
        )}
        {authState === "unauthenticated" && (
          <Link className="px-10" to="signup">
            Signup
          </Link>
        )}
        {authState === "authenticated" && shopId && (
          <>
            <ContainerHeaderSizeToggle />
            <StaticColorDialog />
            {!isViewOnly && (
              <Feature name="isManualCardOnlyEnabled">
                {(enabled) =>
                  enabled ? (
                    <CardFormDialog
                      buttonClasses={"text-sm font-semibold hover:bg-slate-200"}
                      onSubmit={handleManualOnSubmit}
                      buttonIcon={
                        <Icon
                          name={IconNames.add}
                          className="mr-2.5"
                          size={20}
                        />
                      }
                    />
                  ) : (
                    <>
                      <Feature name="isManualCardsEnabled">
                        <CardFormDialog
                          buttonClasses={
                            "text-sm font-semibold hover:bg-slate-200"
                          }
                          onSubmit={handleManualOnSubmit}
                          buttonIcon={
                            <Icon
                              name={IconNames.add}
                              className="mr-2.5"
                              size={20}
                            />
                          }
                        />
                      </Feature>
                      {!isTechnician && (
                        <>
                          <SyncStatus />
                          <LabelSyncStatus />
                        </>
                      )}
                    </>
                  )
                }
              </Feature>
            )}
            <LayoutSelector
              layouts={filteredLayoutsData.map((obj) => Object.keys(obj)[0])}
              selectedLayout={dashboardLayoutId}
              onLayoutChange={handleLayoutSelect}
              hideSearch
            />
            {features.isTechnicianFilterEnabled ||
            features.isServiceAdvisorFilterEnabled ? (
              <DashboardFilters
                onServiceWriterChange={handleServiceWriterChange}
                onTechnicianChange={handleTechnicianChange}
                serviceAdvisorId={filteringServiceAdvisor}
                technicianId={filteringTechnician}
                serviceAdvisors={serviceAdvisors}
                technicians={technicians}
                isServiceAdvisorFilterEnabled={
                  features.isServiceAdvisorFilterEnabled
                }
                isTechnicianFilterEnabled={features.isTechnicianFilterEnabled}
                dueInStart={dueInStart}
                onDueInStartChange={handleDueInStartChange}
                dueInEnd={dueInEnd}
                onDueInEndChange={handleDueInEndChange}
                dueOutEnd={dueOutEnd}
                onDueOutEndChange={handleDueOutEndChange}
                dueOutStart={dueOutStart}
                onDueOutStartChange={handleDueOutStartChange}
                apptStartStart={apptStartStart}
                onApptStartStartChange={handleApptStartStartChange}
                apptStartEnd={apptStartEnd}
                onApptStartEndChange={handleApptStartEndChange}
                apptEndStart={apptEndStart}
                onApptEndStartChange={handleApptEndStartChange}
                apptEndEnd={apptEndEnd}
                onApptEndEndChange={handleApptEndEndChange}
                tags={tags?.tags ?? []}
                filteringTags={filterTags ?? []}
                onFilteringTagsChange={handleFilterTagsChange}
                appliedFiltersCount={appliedFiltersCount}
                onFilteringColorsChange={handleFilterColorsChange}
                colors={shop?.cardConfig?.cardColors ?? DEFAULT_COLORS}
                filteringColors={filterColors ?? []}
                containers={shop?.containers}
                filteringContainer={filterContainer ?? []}
                onFilteringContainerChange={handleFilterContainerChange}
              />
            ) : null}

            <CardSizeControl />

            <ViewToggle />

            <div className="flex w-48 flex-row gap-0">
              <Input
                placeholder="Search"
                variant={"ghost"}
                value={search}
                onChange={(e) => handleSearchStringChange(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className={cn(
                  "h-9 w-full border border-slate-300 bg-slate-200 text-sm placeholder:font-semibold",
                  search !== "" && "rounded-r-none"
                )}
                icon={<Icon name={IconNames.search} size={20} />}
              />

              <button
                onClick={handleClearSearch}
                className={cn(
                  "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-md border border-l-0 border-muted bg-primary text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50",
                  search !== "" ? "visible" : "hidden"
                )}
              >
                <Icon name={IconNames.clear} />
              </button>
            </div>
            {showInboxPanel && (
              <div className="flex flex-row items-center gap-1">
                {features.isTasksTabEnabled && <TasksBellTrigger />}
                <InboxTrigger />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
